import { useState } from "react";
import api from "@/lib/api";
import logger from "@/lib/logger";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Loader2, Download, Upload, Database, CheckCircle2, AlertTriangle, ArrowRight, Info, Wrench, ShieldAlert } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DataMigration() {
  // ── XP Jobs section state ─────────────────────────────────────────────────
  const [exporting,  setExporting]  = useState(false);
  const [importing,  setImporting]  = useState(false);
  const [exportDone, setExportDone] = useState(null);
  const [importDone, setImportDone] = useState(null);
  const [importError,setImportError]= useState(null);
  const [step,       setStep]       = useState(1); // 1=instructions, 2=export, 3=import

  // ── SC Jobs section state ─────────────────────────────────────────────────
  const [scExporting,   setScExporting]   = useState(false);
  const [scImporting,   setScImporting]   = useState(false);
  const [scExportDone,  setScExportDone]  = useState(null);
  const [scImportDone,  setScImportDone]  = useState(null);
  const [scImportError, setScImportError] = useState(null);

  // ── STEP 1: Export all data from THIS environment ────────────────────────
  const handleExport = async () => {
    setExporting(true);
    setExportDone(null);
    try {
      const res = await api.get(`${API}/admin/backup`);
      const data = res.data;

      // Download as JSON file
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement("a");
      a.href     = url;
      a.download = `XSTRONG_Database_Backup_${new Date().toISOString().slice(0,10)}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      setExportDone(data._meta);
      toast.success(`✅ Database exported! ${Object.entries(data._meta?.record_counts||{}).map(([k,v])=>`${k}: ${v}`).join(", ")}`);
    } catch (err) {
      logger.error("[DataMigration] export:", err?.message);
      toast.error("Export failed: " + (err.response?.data?.detail || err.message));
    } finally { setExporting(false); }
  };

  // ── STEP 2: Import data into THIS environment ────────────────────────────
  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";

    setImporting(true);
    setImportDone(null);
    setImportError(null);

    try {
      const text    = await file.text();
      const payload = JSON.parse(text);

      const res = await api.post(`${API}/admin/restore`, payload);
      setImportDone(res.data);
      toast.success(`✅ Data imported! ${res.data.total_inserted} new records, ${res.data.total_updated} updated`);
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || "Unknown error";
      logger.error("[DataMigration] import:", msg);
      setImportError(msg);
      toast.error("Import failed: " + msg);
    } finally { setImporting(false); }
  };

  // ── SC Export ─────────────────────────────────────────────────────────────
  const handleScExport = async () => {
    setScExporting(true);
    setScExportDone(null);
    try {
      const res  = await api.get(`${API}/admin/sc-backup`);
      const data = res.data;
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement("a");
      a.href     = url;
      a.download = `XSTRONG_SC_Jobs_Backup_${new Date().toISOString().slice(0,10)}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setScExportDone(data._meta);
      const count = data._meta?.record_counts?.sc_jobs ?? 0;
      toast.success(`✅ SC Backup exported — ${count} SC Job${count !== 1 ? "s" : ""}`);
    } catch (err) {
      logger.error("[DataMigration] SC export:", err?.message);
      toast.error("SC Export failed: " + (err.response?.data?.detail || err.message));
    } finally { setScExporting(false); }
  };

  // ── SC Import ─────────────────────────────────────────────────────────────
  const handleScImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";
    setScImporting(true);
    setScImportDone(null);
    setScImportError(null);
    try {
      const text    = await file.text();
      const payload = JSON.parse(text);
      const res = await api.post(`${API}/admin/sc-restore`, payload);
      setScImportDone(res.data);
      toast.success(`✅ SC Jobs imported — ${res.data.total_inserted} new, ${res.data.total_updated} updated`);
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || "Unknown error";
      logger.error("[DataMigration] SC import:", msg);
      setScImportError(msg);
      toast.error("SC Import failed: " + msg);
    } finally { setScImporting(false); }
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 max-w-3xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-green-600 flex items-center justify-center">
          <Database size={20} className="text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-semibold text-slate-900" style={{ fontFamily:"'Space Grotesk',sans-serif" }}>
            Data Migration
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Transfer all your data from preview → production (xstrongpower.in)
          </p>
        </div>
      </div>

      {/* How it works */}
      <Card className="rounded-2xl border border-blue-200 bg-blue-50/40 mb-6">
        <CardContent className="p-5">
          <div className="flex items-start gap-3">
            <Info size={16} className="text-blue-600 mt-0.5 flex-shrink-0"/>
            <div>
              <p className="text-sm font-semibold text-blue-900 mb-2">How to transfer your data in 2 steps:</p>
              <div className="space-y-2 text-sm text-blue-800">
                <div className="flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center flex-shrink-0">1</span>
                  <span>Click <strong>"Export Backup"</strong> below — downloads a <code className="bg-blue-100 px-1 rounded">.json</code> file with ALL your data (jobs, billing, inventory, settings)</span>
                </div>
                <div className="flex items-center gap-2">
                  <ArrowRight size={14} className="text-blue-400 ml-3 flex-shrink-0"/>
                  <span className="text-blue-600 text-xs">Then go to your production site →</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center flex-shrink-0">2</span>
                  <span>Open <strong>xstrongpower.in</strong> → go to <strong>Admin Panel → Data Migration</strong> → click <strong>"Import Backup"</strong> → select the file you downloaded</span>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Step 1: Export */}
      <Card className="rounded-2xl border border-slate-200/70 bg-white mb-5">
        <CardContent className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-8 rounded-full bg-green-600 flex items-center justify-center flex-shrink-0">
              <span className="text-white text-sm font-bold">1</span>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-slate-900" style={{ fontFamily:"'Space Grotesk',sans-serif" }}>
                Export Backup — from THIS environment
              </h3>
              <p className="text-xs text-slate-400">Downloads all your data as a single JSON file</p>
            </div>
          </div>

          {exportDone && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-xl">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle2 size={15} className="text-green-600"/>
                <p className="text-sm font-semibold text-green-800">Export successful! File downloaded.</p>
              </div>
              <div className="grid grid-cols-3 gap-2 text-xs">
                {Object.entries(exportDone.record_counts||{}).map(([k,v]) => (
                  <div key={k} className="bg-white border border-green-200 rounded-lg p-2 text-center">
                    <p className="font-bold text-green-700 text-base">{v}</p>
                    <p className="text-slate-500 capitalize">{k}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          <Button
            onClick={handleExport}
            disabled={exporting}
            className="bg-green-600 hover:bg-green-700 text-white w-full sm:w-auto"
            data-testid="export-backup-btn"
          >
            {exporting
              ? <><Loader2 size={16} className="mr-2 animate-spin"/>Exporting...</>
              : <><Download size={16} className="mr-2"/>Export Backup (Download JSON)</>}
          </Button>
        </CardContent>
      </Card>

      {/* Step 2: Import */}
      <Card className="rounded-2xl border border-slate-200/70 bg-white">
        <CardContent className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center flex-shrink-0">
              <span className="text-white text-sm font-bold">2</span>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-slate-900" style={{ fontFamily:"'Space Grotesk',sans-serif" }}>
                Import Backup — into THIS environment
              </h3>
              <p className="text-xs text-slate-400">
                Do this step on <strong>xstrongpower.in</strong> — upload the file from Step 1
              </p>
            </div>
          </div>

          {importDone && (
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-xl">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle2 size={15} className="text-blue-600"/>
                <p className="text-sm font-semibold text-blue-800">Import successful!</p>
              </div>
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div className="bg-white border border-blue-200 rounded-lg p-2 text-center">
                  <p className="font-bold text-blue-700 text-xl">{importDone.total_inserted}</p>
                  <p className="text-slate-500">New records added</p>
                </div>
                <div className="bg-white border border-blue-200 rounded-lg p-2 text-center">
                  <p className="font-bold text-green-700 text-xl">{importDone.total_updated}</p>
                  <p className="text-slate-500">Existing records updated</p>
                </div>
              </div>
              <p className="text-xs text-blue-600 mt-2">
                ✅ Your data is now live on this environment. Go to <a href="/" className="underline font-semibold">Dashboard</a> to verify.
              </p>
            </div>
          )}

          {importError && (
            <div className="mb-4 p-3 bg-rose-50 border border-rose-200 rounded-xl">
              <div className="flex items-center gap-2">
                <AlertTriangle size={15} className="text-rose-600"/>
                <p className="text-sm text-rose-800">Import failed: {importError}</p>
              </div>
            </div>
          )}

          <label className="cursor-pointer">
            <input type="file" accept=".json" className="hidden" onChange={handleImport} disabled={importing} data-testid="import-backup-input"/>
            <Button
              as="span"
              variant="outline"
              disabled={importing}
              className="border-blue-200 text-blue-700 hover:bg-blue-50 w-full sm:w-auto pointer-events-none"
              data-testid="import-backup-btn"
            >
              {importing
                ? <><Loader2 size={16} className="mr-2 animate-spin"/>Importing...</>
                : <><Upload size={16} className="mr-2"/>Import Backup (Upload JSON)</>}
            </Button>
          </label>

          <p className="text-xs text-slate-400 mt-2">
            ⚠️ This is safe to run multiple times — existing records are updated, not duplicated.
          </p>
        </CardContent>
      </Card>

      {/* ════════════════════════════════════════════════════════════════════ */}
      {/* SC DATA MIGRATION — completely separate from All Jobs above          */}
      {/* ════════════════════════════════════════════════════════════════════ */}
      <div className="my-8 flex items-center gap-3">
        <Separator className="flex-1" />
        <span className="text-xs font-bold text-slate-400 uppercase tracking-widest px-2 flex-shrink-0">
          Service Centre Section
        </span>
        <Separator className="flex-1" />
      </div>

      {/* SC Header */}
      <div className="flex items-center gap-3 mb-5">
        <div className="w-10 h-10 rounded-xl bg-green-600 flex items-center justify-center">
          <Wrench size={18} className="text-white" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily:"'Space Grotesk',sans-serif" }}>
            SC Jobs Data Migration
          </h2>
          <p className="text-xs text-slate-500 mt-0.5">
            Service Centre Jobs only — completely separate from All Jobs above
          </p>
        </div>
      </div>

      {/* SC Warning banner */}
      <Card className="rounded-2xl border border-amber-200 bg-amber-50/50 mb-5">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <ShieldAlert size={16} className="text-amber-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-amber-900 space-y-1">
              <p className="font-semibold">This section is for Service Centre Jobs only (SC-XXXX records).</p>
              <ul className="text-xs text-amber-800 space-y-0.5 list-disc list-inside">
                <li>Export SC Backup downloads <strong>only sc_jobs</strong> — never touches All Jobs, Billing, Inventory, or Settings</li>
                <li>Import SC Backup restores <strong>only sc_jobs</strong> — existing SC records are updated, not duplicated</li>
                <li>Use the <strong>Export Backup / Import Backup</strong> section above to migrate All Jobs data</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* SC Export */}
      <Card className="rounded-2xl border border-slate-200/70 bg-white mb-5">
        <CardContent className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-8 rounded-full bg-green-600 flex items-center justify-center flex-shrink-0">
              <Download size={15} className="text-white" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-slate-900" style={{ fontFamily:"'Space Grotesk',sans-serif" }}>
                Export SC Backup
              </h3>
              <p className="text-xs text-slate-400">Downloads all SC Jobs as a JSON file — SC data only</p>
            </div>
          </div>

          {scExportDone && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-xl">
              <div className="flex items-center gap-2 mb-1">
                <CheckCircle2 size={14} className="text-green-600" />
                <p className="text-sm font-semibold text-green-800">SC Export successful! File downloaded.</p>
              </div>
              <p className="text-xs text-green-700">
                {scExportDone.record_counts?.sc_jobs ?? 0} SC Job{(scExportDone.record_counts?.sc_jobs ?? 0) !== 1 ? "s" : ""} exported
              </p>
            </div>
          )}

          <Button
            onClick={handleScExport}
            disabled={scExporting}
            className="bg-green-600 hover:bg-green-700 text-white w-full sm:w-auto"
            data-testid="sc-export-backup-btn"
          >
            {scExporting
              ? <><Loader2 size={16} className="mr-2 animate-spin" />Exporting SC Jobs...</>
              : <><Download size={16} className="mr-2" />Export SC Backup (SC Jobs only)</>}
          </Button>
        </CardContent>
      </Card>

      {/* SC Import */}
      <Card className="rounded-2xl border border-slate-200/70 bg-white">
        <CardContent className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center flex-shrink-0">
              <Upload size={15} className="text-white" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-slate-900" style={{ fontFamily:"'Space Grotesk',sans-serif" }}>
                Import SC Backup
              </h3>
              <p className="text-xs text-slate-400">
                Restores SC Jobs from an exported SC backup file — does not affect All Jobs
              </p>
            </div>
          </div>

          {scImportDone && (
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-xl">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle2 size={14} className="text-blue-600" />
                <p className="text-sm font-semibold text-blue-800">SC Import successful!</p>
              </div>
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div className="bg-white border border-blue-200 rounded-lg p-2 text-center">
                  <p className="font-bold text-blue-700 text-xl">{scImportDone.total_inserted}</p>
                  <p className="text-slate-500">New SC records added</p>
                </div>
                <div className="bg-white border border-blue-200 rounded-lg p-2 text-center">
                  <p className="font-bold text-green-700 text-xl">{scImportDone.total_updated}</p>
                  <p className="text-slate-500">Existing SC records updated</p>
                </div>
              </div>
              <p className="text-xs text-blue-600 mt-2">
                ✅ SC Jobs are now live. Go to{" "}
                <a href="/sc" className="underline font-semibold">Service Centre Jobs</a> to verify.
              </p>
            </div>
          )}

          {scImportError && (
            <div className="mb-4 p-3 bg-rose-50 border border-rose-200 rounded-xl">
              <div className="flex items-center gap-2">
                <AlertTriangle size={14} className="text-rose-600" />
                <p className="text-sm text-rose-800">{scImportError}</p>
              </div>
            </div>
          )}

          <label className="cursor-pointer">
            <input
              type="file"
              accept=".json"
              className="hidden"
              onChange={handleScImport}
              disabled={scImporting}
              data-testid="sc-import-backup-input"
            />
            <Button
              as="span"
              variant="outline"
              disabled={scImporting}
              className="border-blue-200 text-blue-700 hover:bg-blue-50 w-full sm:w-auto pointer-events-none"
              data-testid="sc-import-backup-btn"
            >
              {scImporting
                ? <><Loader2 size={16} className="mr-2 animate-spin" />Importing SC Jobs...</>
                : <><Upload size={16} className="mr-2" />Import SC Backup (SC Jobs only)</>}
            </Button>
          </label>

          <p className="text-xs text-slate-400 mt-2">
            ⚠️ Only accepts files exported by <strong>Export SC Backup</strong> above.
            Safe to run multiple times — existing SC records are updated, not duplicated.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
